import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import { addDays, format, startOfWeek } from 'date-fns';

import { globalColors } from 'globalStyle';
import { ReactComponent as BackArrowIcon } from '../../assets/icons/prevDateIcon.svg';
import { ReactComponent as NextArrowIcon } from '../../assets/icons/nextDateIcon.svg';
import { ReactComponent as NewCalendarIcon } from '../../assets/icons/newCalendarIcon.svg';
import DatePickerPopUp from './DatePickerPopUp';
import { MainReportTypes, ReportTypesNew, TimeFrame } from 'models/ReportData';
import { AnalyticsInteractionMetadata, CustomTimeFrameProperties } from 'actions/reportFilterActions';
import {
  DATE_PICKER_NEXT_BUTTON_ELEMENT_ID,
  DATE_PICKER_OPEN_BUTTON_ELEMENT_ID,
  DATE_PICKER_PREV_BUTTON_ELEMENT_ID,
} from 'KonnectAnalytics/analyticsElementIds';
import { KonnectAnalytics } from 'KonnectAnalytics/konnectAnalytics';
import { convertTo12HourFormat } from 'helpers/conversions';
const StyledButton = styled(Button)`
  margin: 0 15px;
  font-family: 'Rubik';
  font-style: normal;
  font-weight: 400;
  font-size: 12px;
  line-height: 14px;
  min-width: 220px;
  height: 30px;

  &:hover {
    color: ${globalColors.regular.text.primary};
  }
`;
const ArrowWrapper = styled.div<{ isDisable: boolean }>`
  display: flex;
  place-content: center;
  width: 30px;
  height: 30px;
  border-radius: 4px;
  border:1px solid ${props =>
    props.isDisable
      ? globalColors.regular.buttons.disableBorder
      : globalColors.regular.buttons.regularBorder}; };
  opacity: ${props => (props.isDisable ? 0.5 : 1)};
`;
type NewDatePickerPropsType = {
  onHandleLeft: () => void;
  onHandleRight: () => void;
  setActiveDate: (date: Date) => void;
  setIsCustomTimeFrameOpen?: (isOpen: boolean) => void;
  setTimeframe?: (
    timeframe: TimeFrame,
    startDate?: Date,
    customTimeFrameProperties?: CustomTimeFrameProperties,
    analyticsInteractionMetadata?: AnalyticsInteractionMetadata
  ) => void;
  disableLeft: boolean;
  disableRight: boolean;
  date: Date;
  timeframe: TimeFrame;
  prevTimeframe?: TimeFrame;
  lastUpdate: Date;
  selectedDate: Date;
  startDate: Date;
  customTimeframeProperties?: CustomTimeFrameProperties;
  activeReport: ReportTypesNew;
};
const NewDatePicker = (props: NewDatePickerPropsType) => {
  const {
    onHandleLeft,
    onHandleRight,
    disableLeft,
    disableRight,
    date,
    timeframe,
    prevTimeframe,
    lastUpdate,
    selectedDate,
    startDate,
    customTimeframeProperties,
    setActiveDate,
    activeReport,
    setIsCustomTimeFrameOpen,
    setTimeframe,
  } = props;

  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);

  useEffect(() => {
    //*this component is always rendered with a hardcoded timeframe==='custom' prop, so that alone
    //*can't tell a genuine "user just clicked Custom" mount from an incidental remount (e.g. caused by
    //*the machine filter toggling the parent's empty-state branch off and on). prevTimeframe carries the
    //*real redux timeframe at mount time, which is still not 'custom' the first time the user opens it -
    //*only open automatically in that case, not when custom was already the active timeframe.
    if (timeframe === 'custom' && prevTimeframe !== 'custom') {
      setIsDatePickerOpen(true);
      KonnectAnalytics.toggleDatePickerCalendar({
        element_id: DATE_PICKER_OPEN_BUTTON_ELEMENT_ID,
        interaction_type: 'toggle',
        state: 'opened',
      });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const [isHovered, setIsHovered] = useState(false);

  const getDateStringForCustomTimeframe = (
    customTimeframeProperties: CustomTimeFrameProperties,
    timeframe: TimeFrame
  ) => {
    if (
      !customTimeframeProperties.customTimeFrameStartDate ||
      !customTimeframeProperties.customTimeFrameEndDate
    ) {
      return 'Custom Calendar';
    } else {
      const startDate = customTimeframeProperties.customTimeFrameStartDate;
      const endDate = customTimeframeProperties.customTimeFrameEndDate;
      return `${format(startDate, 'd')} ${format(startDate, 'MMM')}, ${format(
        startDate,
        'yyyy'
      )}, ${convertTo12HourFormat(customTimeframeProperties.customTimeFrameStartHour)} - ${format(
        endDate,
        'd'
      )} ${format(endDate, 'MMM')}, ${format(endDate, 'yyyy')}, ${convertTo12HourFormat(
        customTimeframeProperties.customTimeFrameEndHour
      )}`;
    }
  };
  const getDateString = timeframe => {
    switch (timeframe) {
      case 'day':
        return `${format(date, 'd')} ${format(date, 'MMM')}, ${format(date, 'yyyy')}`;

      case 'week':
        const start = startOfWeek(date, { weekStartsOn: 1 });
        const endOfWeek = addDays(start, 6);
        return `${format(start, 'd')}-${format(endOfWeek, 'd')} ${format(
          endOfWeek,
          'MMM'
        )}, ${format(endOfWeek, 'yyyy')}`;
      case 'month':
        return `${format(date, 'MMM')},${format(date, 'yyyy')}`;
      case 'quarter':
        return `${format(date, `QQQ`)},${format(date, 'yyyy')}`;
      case 'year':
        return `${format(date, 'yyyy')}`;
      case 'custom':
        return getDateStringForCustomTimeframe(customTimeframeProperties, timeframe);
      default:
        return `${format(date, 'd')} ${format(date, 'MMM')}, ${format(date, 'yyyy')}`;
    }
  };
  return (
    <>
      <ArrowWrapper
        data-testid={DATE_PICKER_PREV_BUTTON_ELEMENT_ID}
        isDisable={disableLeft}
        onClick={onHandleLeft}>
        <IconButton size="large">
          <BackArrowIcon
            stroke={
              disableLeft
                ? globalColors.regular.buttons.disableText
                : globalColors.regular.buttons.regularText
            }
          />
        </IconButton>
      </ArrowWrapper>
      <StyledButton
        data-testid={DATE_PICKER_OPEN_BUTTON_ELEMENT_ID}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => {
          setIsHovered(false);
        }}
        style={{
          textTransform: 'none',
          color: isDatePickerOpen
            ? globalColors.regular.buttons.whiteText
            : globalColors.regular.buttons.regularText,
          backgroundColor: isDatePickerOpen ? globalColors.regular.background.blue : 'transparent',
          border: isDatePickerOpen
            ? globalColors.regular.background.blue
            : `1px solid ${globalColors.regular.buttons.regularBorder}`,
          whiteSpace: 'nowrap',
          padding: '0 10px',
        }}
        startIcon={
          <NewCalendarIcon
            fill={
              isDatePickerOpen && !isHovered
                ? globalColors.regular.background.white
                : globalColors.regular.background.blue
            }
          />
        }
        disabled={false}
        onClick={() => {
          const nextState = isDatePickerOpen ? 'closed' : 'opened';

          setIsDatePickerOpen(!isDatePickerOpen);
          KonnectAnalytics.toggleDatePickerCalendar({
            element_id: DATE_PICKER_OPEN_BUTTON_ELEMENT_ID,
            interaction_type: 'toggle',
            state: nextState,
          });
        }}>
        {getDateString(timeframe)}
      </StyledButton>
      <ArrowWrapper
        data-testid={DATE_PICKER_NEXT_BUTTON_ELEMENT_ID}
        isDisable={disableRight}
        onClick={onHandleRight}>
        <IconButton size="large">
          <NextArrowIcon
            stroke={
              disableRight
                ? globalColors.regular.buttons.disableText
                : globalColors.regular.buttons.regularText
            }
          />
        </IconButton>
      </ArrowWrapper>
      <DatePickerPopUp
        date={date}
        customTimeframeProperties={customTimeframeProperties}
        isDatePickerOpen={isDatePickerOpen}
        timeframe={timeframe}
        prevTimeframe={prevTimeframe}
        lastUpdate={lastUpdate}
        selectedDate={selectedDate}
        startDate={startDate}
        activeReport={activeReport}
        setIsDatePickerOpen={setIsDatePickerOpen}
        setActiveDate={setActiveDate}
        setIsCustomTimeFrameOpen={setIsCustomTimeFrameOpen}
        setTimeframe={setTimeframe}
      />
    </>
  );
};

export default NewDatePicker;
